// Menu konta w górnej belce AppShell — widoczne, gdy JEST sesja (niezależnie od
// flagi AUTH_REQUIRED; niezalogowanemu przy wyłączonej fladze nic nie dokłada).
// Wyzwalacz jak w projekcie belki aplikacji: imię i nazwisko + role na ciemnej belce,
// krąg z inicjałami w kolorze akcentu.

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { accountLabel, authApi, isAdmin, type Me } from '../../lib/auth'
import { RoleChips } from './AuthUi'

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const letters = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : (parts[0] ?? '?').slice(0, 2)
  return letters.toUpperCase()
}

const ITEM = 'block w-full rounded-md px-3 py-1.5 text-left text-sm text-ink-700 hover:bg-ink-50 hover:text-ink-900'

export function UserMenu({ me, onLoggedOut }: { me: Me; onLoggedOut: () => void }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const roles = me.user.roles.map((r) => me.role_labels[r] ?? r).join(' · ')

  const logout = () => {
    setBusy(true)
    authApi.logout().then(
      () => { setBusy(false); setOpen(false); onLoggedOut() },
      () => setBusy(false),
    )
  }

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2.5 rounded-xl py-1 pl-2 pr-1 text-white transition-colors hover:bg-white/[0.06]"
      >
        <span className="text-right leading-tight">
          <span className="block max-w-[12rem] truncate text-[13px] font-bold">{me.user.display_name}</span>
          {roles && <span className="block max-w-[12rem] truncate text-[11px] text-white/60">{roles}</span>}
        </span>
        <span className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full bg-white text-[13px] font-extrabold text-forest-800">
          {initials(me.user.display_name)}
        </span>
      </button>

      {open && (
        <div role="menu" className="absolute right-0 top-full z-40 mt-1.5 w-64 rounded-xl border border-ink-200 bg-white p-1.5 shadow-card">
          <div className="border-b border-ink-100 px-3 pb-2 pt-1.5">
            <div className="text-sm font-semibold text-ink-900">{me.user.display_name}</div>
            <div className="truncate font-mono text-[11px] text-ink-400">{accountLabel(me.user)}</div>
            <div className="mt-1.5">
              <RoleChips roles={me.user.roles} labels={me.role_labels} />
            </div>
          </div>
          <div className="pt-1">
            <Link role="menuitem" to="/konto" onClick={() => setOpen(false)} className={ITEM}>Zmień hasło</Link>
            {isAdmin(me) && (
              <Link role="menuitem" to="/admin" onClick={() => setOpen(false)} className={ITEM}>Panel admina</Link>
            )}
            <button role="menuitem" type="button" onClick={logout} disabled={busy} className={`${ITEM} disabled:opacity-50`}>
              {busy ? 'Wylogowywanie…' : 'Wyloguj'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
